import { SearchspringSearchProvider } from 'libs/searchspring/fetchers/search'

import clsx from 'clsx'

import { useEffect, useState } from 'preact/hooks'
import { useScrollCache } from 'libs/searchspring/hooks/useScrollCache'
import { useFilterScroll } from 'libs/searchspring/hooks/useFilterScroll'
import { useScrollPositionKeeper } from 'libs/searchspring/hooks/useScrollPositionKeeper'
import { scrollHistoryManager } from 'helpers/scroll-history'
import { useInfiniteScrollResults } from 'libs/searchspring/hooks/useInfiniteScrollResults'
import { InfiniteScrollNext, InfiniteScrollPrevious } from 'libs/searchspring/components/collection/InfiniteScroll'
import { useOption } from 'libs/searchspring/hooks/useOption'
import { FiltersProvider } from 'libs/searchspring/hooks/useFilters'
import { CollectionToolbar } from 'libs/searchspring/components/collection/CollectionToolbar'
import { FilterDrawer } from 'libs/searchspring/components/collection/filters/FilterDrawer'
import { SearchHeader } from 'libs/searchspring/components/SearchHeader'
import { ResultItem } from 'libs/searchspring/components/ResultCardWrapper'
import { SkeletonCard } from 'libs/searchspring/components/SkeletonCard'
import { calculateProductCount } from 'libs/searchspring/components/collection/filters/ProductCount'

const GRID_IMAGE_SIZES = '(min-width: 1828px) 25vw, (min-width: 768px) 33vw, 100vw'
const GRID_IMAGE_WIDTHS = [200, 320, 480, 640, 800, 1000, 1200, 1440]

function SearchGrid() {
  history.scrollRestoration = 'manual'
  const [firstLoad, setFirstLoad] = useState(true)
  const [showSkeleton, setShowSkeleton] = useState(true)
  const { allResults, gridChange, isLoading, results } = useInfiniteScrollResults()
  const { setEntry } = useScrollPositionKeeper(gridChange)
  const { setScrollElement } = useFilterScroll()
  const query = useOption(
    'query',
    { syncWithURL: true }
  )

  const { corrected = '', original = '' } = results?.query || {}
  const didYouMean = results?.didYouMean?.query || ''

  const corrections = {
    corrected,
    original,
    didYouMean
  }

  useEffect(() => {
    if (!isLoading) setFirstLoad(false)
  }, [isLoading])

  useEffect(() => {
    if (!isLoading && allResults?.length > 0) {
      const timer = setTimeout(() => {
        setShowSkeleton(false)
      }, 150)

      return () => clearTimeout(timer)
    } else if (isLoading || firstLoad) {
      setShowSkeleton(true)
    }
  }, [isLoading, allResults?.length, firstLoad])

  useScrollCache([allResults, firstLoad])

  const totalResults = calculateProductCount(results)

  return (
    <div className="grow flex flex-col">
      <SearchHeader query={query} totalResults={totalResults} isLoading={isLoading} corrections={corrections}/>
      <FiltersProvider>
        {allResults.length > 0 && (
          <>
            <CollectionToolbar />
            <FilterDrawer />
          </>
        )}
        <div className="grow flex flex-col">
          <div
            ref={(el) => { setScrollElement(el); setEntry(el) }}
            className={clsx(
              'grid grid-cols-1 md:grid-cols-3 xxl:grid-cols-4 [grid-auto-flow:row_dense] gap-px w-full overflow-hidden',
              allResults.length > 0 && 'pb-6.75',
              isLoading && firstLoad && 'min-h-screen-height'
            )}
          >
            <InfiniteScrollPrevious />
            {showSkeleton && firstLoad ? (
              Array.from({ length: window.settings?.per_page || 12 }, (_, index) => (
                <SkeletonCard key={`skeleton-${index}`} className="w-full" />
              ))
            ) : (
              allResults?.map((item, index) => {
                return (
                  <ResultItem
                    item={item}
                    key={item.handle}
                    className="wk-wishlist-button-target"
                    dataIndex={index.toString()}
                    eager={index === 0}
                    imageSizes={GRID_IMAGE_SIZES}
                    imageWidths={GRID_IMAGE_WIDTHS}
                  />
                )
              })
            )}
            {allResults.length > 0 && !firstLoad && (
              <InfiniteScrollNext />
            )}
          </div>
        </div>
      </FiltersProvider>
    </div>
  )
}

export function WrappedSearchGrid() {
  scrollHistoryManager.updateHistoryState()
  const urlParams = new URLSearchParams(window.location.search)
  const queryParam = urlParams.get('query')
  return (
    <SearchspringSearchProvider query={queryParam}>
      <SearchGrid />
    </SearchspringSearchProvider>
  )
}
